/**
 * Module Web Worker running buildRibbonMeshes off the main thread (PLAN.md: geometry builders run in workers; the
 * main thread only uploads buffers).
 *
 * Protocol (one worker, many models):
 *   'model'   topology of a model, sent once per NanomeModel (atom names, chain names, residue table);
 *   'build'   current coordinates + the component's residues + one colour per residue + build options;
 *   'release' forget a model (the client sends it when the NanomeModel is garbage-collected or disposed).
 * Results carry the mesh buffers as transferables. Colours are resolved on the main thread before the request
 * (the engine's colour callback cannot cross threads), so a build is a pure function of its message.
 *
 * The handler is a plain function over a port so client.ts can run the same protocol in-thread when Worker is
 * unavailable and tests can drive it with a fake port.
 */
import { buildRibbonMeshes } from './mesh';
import type { RibbonBuildOptions, RibbonMeshBuffers, RibbonSourceModel } from './types';

/** Everything extractSegments reads from a model except the coordinates. */
export interface RibbonTopology {
  atomCount: number;
  atomName: string[];
  chainName: string[];
  residues: RibbonSourceModel['residues'];
}

export type ToRibbonWorker =
  | { type: 'model'; modelId: number; topology: RibbonTopology }
  | {
    type: 'build'; id: number; modelId: number;
    /** current frame, Å, 3 * atomCount (a copy: transferred) */
    xyz: Float32Array;
    /** model residue indices (polymer residues of the component) */
    residueIndices: Uint32Array;
    /** rgb per entry of residueIndices (trace-atom colour under the component's coloring) */
    colors: Float32Array;
    options: RibbonBuildOptions;
  }
  | { type: 'release'; modelId: number };

export type FromRibbonWorker =
  | { type: 'result'; id: number; meshes: RibbonMeshBuffers[] }
  | { type: 'error'; id: number; message: string };

export interface RibbonWorkerPort {
  postMessage(msg: FromRibbonWorker, transfer?: Transferable[]): void;
}

/** Distinct ArrayBuffers of the meshes, for postMessage transfer lists. */
export function ribbonTransferables(meshes: readonly RibbonMeshBuffers[]): ArrayBuffer[] {
  const set = new Set<ArrayBufferLike>();
  for (const m of meshes) for (const a of [m.position, m.normal, m.color, m.residueIndex, m.index]) set.add(a.buffer);
  return [...set].filter((b): b is ArrayBuffer => b instanceof ArrayBuffer && b.byteLength > 0);
}

/** Topology of a model (copies nothing; postMessage structured-clones it). */
export function ribbonTopology(model: RibbonSourceModel): RibbonTopology {
  const r = model.residues;
  return {
    atomCount: model.atomCount,
    atomName: model.atomName,
    chainName: model.chains.name,
    residues: { count: r.count, atomStart: r.atomStart, atomCount: r.atomCount, chainIndex: r.chainIndex, name: r.name, serial: r.serial, insCode: r.insCode, ss: r.ss, substructure: r.substructure },
  };
}

/**
 * Message handler for one worker scope. `transfer` false (in-thread use) keeps the result buffers owned by the
 * caller instead of transferring them.
 */
export function createRibbonWorkerHandler(port: RibbonWorkerPort, options: { transfer?: boolean } = {}): (msg: ToRibbonWorker) => void {
  const transfer = options.transfer ?? true;
  const models = new Map<number, RibbonTopology>();
  return (msg: ToRibbonWorker) => {
    switch (msg.type) {
      case 'model':
        models.set(msg.modelId, msg.topology);
        break;
      case 'release':
        models.delete(msg.modelId);
        break;
      case 'build': {
        const topo = models.get(msg.modelId);
        if (!topo) { port.postMessage({ type: 'error', id: msg.id, message: `unknown model ${msg.modelId}` }); return; }
        if (msg.xyz.length !== topo.atomCount * 3) { port.postMessage({ type: 'error', id: msg.id, message: `xyz has ${msg.xyz.length / 3} atoms, model has ${topo.atomCount}` }); return; }
        try {
          const model: RibbonSourceModel = { atomCount: topo.atomCount, atomName: topo.atomName, xyz: msg.xyz, chains: { name: topo.chainName }, residues: topo.residues };
          const slot = new Int32Array(topo.residues.count).fill(-1);
          for (let k = 0; k < msg.residueIndices.length; k++) slot[msg.residueIndices[k]!] = k;
          const colors = msg.colors;
          const colorOf = (ri: number): [number, number, number] => {
            const k = slot[ri] ?? -1;
            return k < 0 ? [1, 1, 1] : [colors[k * 3]!, colors[k * 3 + 1]!, colors[k * 3 + 2]!];
          };
          const meshes = buildRibbonMeshes(model, msg.residueIndices, colorOf, msg.options);
          port.postMessage({ type: 'result', id: msg.id, meshes }, transfer ? ribbonTransferables(meshes) : undefined);
        } catch (e) {
          port.postMessage({ type: 'error', id: msg.id, message: e instanceof Error ? e.message : String(e) });
        }
        break;
      }
    }
  };
}

// Self-register when loaded as a dedicated worker (not on the main thread, not in Node).
const g = globalThis as unknown as { WorkerGlobalScope?: new () => object; self?: unknown; postMessage?: (m: unknown, t?: Transferable[]) => void; onmessage?: ((e: MessageEvent<ToRibbonWorker>) => void) | null };
if (typeof g.WorkerGlobalScope === 'function' && g.self instanceof g.WorkerGlobalScope && typeof g.postMessage === 'function') {
  const post = g.postMessage.bind(globalThis);
  const handle = createRibbonWorkerHandler({ postMessage: (msg, t) => post(msg, t) });
  g.onmessage = (e) => handle(e.data);
}
